import {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { padding } from "csx";
import { style } from "typestyle";
import { Colours, Sizes, boxShadow, fontFamily, zIndex } from "./styles";

export {
  Colours,
  Sizes,
  fontFamily,
  boxShadow,
  scrollClass,
  zIndex,
} from "./styles";
export const Styles = { Colours, boxShadow };

export * from "./components/Login";
export * from "./components/Form";
export * from "./components/Inputs";
export * from "./components/Button";
export * from "./components/Loader";
export type { IconProps } from "./components/Icon/Icon";
export { Icon } from "./components/Icon/Icon";
export { DateRangePicker } from "./components/DateRangePicker/DateRangePicker";
export type { DateRangePickerProps } from "./components/DateRangePicker/DateRangePicker";

interface HeadingProps {
  size?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

export const Heading: FC<HeadingProps> = ({
  size = 1,
  className,
  children,
  style,
}) => {
  const Tag = `h${size}` as keyof JSX.IntrinsicElements;
  return (
    <Tag className={className} style={{ color: Colours.white, ...style }}>
      {children}
    </Tag>
  );
};

interface DropdownOption {
  key: string;
  text: ReactNode;
  onClick?: () => void;
}

interface DropdownWrapperProps {
  options: DropdownOption[][];
  children?: ReactNode;
  direction?: "left" | "right" | "up" | "down";
  alt?: boolean;
}

const dropdownMenuContainer = style({
  fontFamily,
  textTransform: "uppercase",
  maxHeight: 336,
  overflowY: "auto",
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  $nest: {
    "&::-webkit-scrollbar": { display: "none" },
    "&.alt": {
      background: Colours.darkBlue,
      border: `1px solid ${Colours.lighterBlue}`,
      borderRadius: Sizes.borderRadius,
    },
  },
});

const dropdownMenuList = style({
  display: "block",
  minWidth: "max-content",
  cursor: "pointer",
  boxShadow,
  fontFamily,
});

const dropdownMenuItem = style({
  color: Colours.textLight,
  background: Colours.darkBlue,
  padding: padding(5, 10),
  cursor: "pointer",
  textTransform: "none",
  $nest: {
    "&:hover": { background: Colours.primary, color: "#16181D" },
    "&.alt": {
      padding: padding(8, 16),
      $nest: {
        "&:hover": { background: Colours.blue, color: Colours.textLight },
      },
    },
  },
});

export const DropdownWrapper: FC<DropdownWrapperProps> = ({
  options,
  children,
  direction = "right",
  alt,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const horizontalAnchor = direction === "left" ? { right: 0 } : { left: 0 };

  return (
    <div
      ref={ref}
      style={{ display: "inline-block", position: "relative" }}
      data-testid="dropdown-wrapper"
    >
      <div onClick={() => setOpen((s) => !s)}>{children}</div>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            ...horizontalAnchor,
            zIndex: zIndex.highest,
            boxShadow,
            borderRadius: alt ? Sizes.borderRadius : 0,
          }}
          data-testid="dropdown-menu-list"
        >
          <div className={[dropdownMenuContainer, alt ? "alt" : ""].join(" ")}>
            <div className={dropdownMenuList} data-testid="menu-item-list">
              {options.flat().map((opt) => (
                <div
                  key={opt.key}
                  className={[dropdownMenuItem, alt ? "alt" : ""].join(" ")}
                  onClick={() => {
                    opt.onClick?.();
                    setOpen(false);
                  }}
                >
                  {opt.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export interface ToastNotificationsApi {
  addToast: (msg: string, type?: "info" | "success" | "warn" | "error") => void;
}

export const useToastNotifications = (): ToastNotificationsApi => ({
  addToast: (msg) => {
    if (typeof window !== "undefined") {
      console.warn("[mfe-shell] toast:", msg);
    }
  },
});
